import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@shared/ui/button";
import { ResourceFilters } from "../../types";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useGetUtilityServices, useGetResourceTypeList, useGetResourceSubtypeList } from "@features/mx/hooks";

interface FilterPanelProps {
  currentFilters: ResourceFilters;
  onApplyFilters: (filters: ResourceFilters) => void;
  onResetFilters: () => void;
}

export function FilterPanel({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}: FilterPanelProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const [watchResourceType, setWatchResourceType] = useState<string>("");

  const { data: servicesData } = useGetUtilityServices(remoteUtilityId);
  const { data: resourceTypeResponse } = useGetResourceTypeList(remoteUtilityId);
  const { data: subtypesResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    watchResourceType && watchResourceType !== "all" ? [watchResourceType] : undefined
  );

  const utilityServiceOptions = useMemo(() => {
    const base = [{ label: "All services", value: "all" }];
    const options = Array.isArray(servicesData)
      ? servicesData.map((item: any) => ({
          label: item.name,
          value: item.name,
        }))
      : [];
    return [...base, ...options];
  }, [servicesData]);

  const resourceTypeOptions = useMemo(() => {
    const base = [{ label: "All types", value: "all" }];
    const options = Array.isArray(resourceTypeResponse?.result)
      ? resourceTypeResponse.result.map((item: any) => ({
          label: item.name,
          value: item.code,
        }))
      : [];
    return [...base, ...options];
  }, [resourceTypeResponse]);

  const subtypesList = useMemo(() => {
    const base = [{ label: "All sub types", value: "all" }];
    const options = Array.isArray(subtypesResponse?.result)
      ? subtypesResponse.result.map((item: any) => ({
          label: item.name,
          value: item.code,
        }))
      : [];
    return [...base, ...options];
  }, [subtypesResponse]);

  const formFields: FormField[] = [
    {
      name: "utilityService",
      label: "Utility service",
      type: "select",
      required: false,
      placeholder: "Select utility service",
      options: utilityServiceOptions,
      className: "col-span-1",
    },
    {
      name: "type",
      label: "Type",
      type: "select",
      required: false,
      placeholder: "Select type",
      options: resourceTypeOptions,
      className: "col-span-1",
    },
    {
      name: "subType",
      label: "Sub type",
      type: "select",
      required: false,
      placeholder: "Select sub type",
      options: subtypesList,
      className: "col-span-1",
    },
    {
      name: "status",
      label: "Assignment status",
      type: "select",
      required: false,
      placeholder: "Select status",
      options: [
        { label: "All statuses", value: "all" },
        { label: "Assigned", value: "Assigned" },
        { label: "Unassigned", value: "Unassigned" },
      ],
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: {
      utilityService: currentFilters.utilityService || "all",
      type: currentFilters.type || "all",
      subType: currentFilters.subType || "all",
      status: currentFilters.status || "all",
    },
  });

  const currentType = form.watch("type");
  useEffect(() => {
    setWatchResourceType(currentType || "");
  }, [currentType]);

  const onSubmit = (data: ResourceFilters) => {
    // Convert "all" back to undefined for actual filtering
    const cleanedFilters: ResourceFilters = {};
    if (data.utilityService && data.utilityService !== "all") cleanedFilters.utilityService = data.utilityService;
    if (data.type && data.type !== "all") cleanedFilters.type = data.type;
    if (data.subType && data.subType !== "all") cleanedFilters.subType = data.subType;
    if (data.status && data.status !== "all") cleanedFilters.status = data.status;

    onApplyFilters(cleanedFilters);
  };

  const handleReset = () => {
    form.reset({
      utilityService: "all",
      type: "all",
      subType: "all",
      status: "all",
    });
    onResetFilters();
  };

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-2 pt-2 border-t mt-4 col-span-4">
      <Button type="button" variant="outline" size="sm" onClick={handleReset}>
        Reset
      </Button>
      <Button type="submit" size="sm">
        Apply filters
      </Button>
    </div>
  );

  return (
    <DynamicForm
      fields={formFields}
      form={form}
      onSubmit={onSubmit}
      gridColumns={4}
      config={{
        showSubmitButton: false,
        classes: FIELD_CLASSES,
      }}
      renderCustomFooter={renderCustomFooter}
    />
  );
}
